import type { MeetingModel } from 'src/store/selectors';
import { getOrganization, getOrganizationId, meetings } from 'src/store/selectors';
import type { State } from 'src/store/types.js';
import { LocalDate } from 'src/utilities/local-date.js';
import { toDateTime } from 'src/store/utilities';
import type { ApiClient } from 'src/store/backend';
import { getPromoDataForEntity } from 'src/store/selectors/promo';
import type { Occurrence } from '../../library/lists/occurrence';
import type { CommonDataEntityView } from '../../content/common-data-entity-view';
import type { MeetingView } from '../../content/meetings/meeting-view';
import { assertIsDefined } from '../../assertIsDefined';
import { assertEntityFound } from './entity-not-found';
import { getFeatureStates } from '../../store/selectors/features/getFeatureStates';
import { t } from 'src/i18n';

function getDefaultNewMeeting(commonData: CommonDataEntityView): MeetingView {
  return {
    ...commonData,
    docsForLinking: [],
    fetchDraft(): Promise<Record<string, never>> {
      throw new Error('Illegal state, not applicable');
    },
    hasDraft: false,
    isConfirmedEntity: true,
    currentUserHasAccess: true,
    helpContent: '',
    attachments: [],
    deleted: false,
    href: '',
    name: '',
    parentHref: '',
    pdfHref: '',
    type: 'meetings',
    uuid: '',
    occurrences: [],
    today: LocalDate.now(),
    interviewedEmployeeUuid: '',
    interviewedEmployeeName: '',
    deletable: true,
    currentUserHasWriteAccess: true,
  };
}

function toMeetingView(
  state: State,
  commonData: CommonDataEntityView,
  currentParent: string,
  meeting: MeetingModel,
): MeetingView {
  const occurrences: Occurrence[] = meeting.meetingOccurrences.map((e) => {
    return {
      occurrenceUuid: e.uuid,
      dateTime: toDateTime(e.date, e.time),
      durationMinutes: e.durationMinutes,
      href: e.restricted
        ? ''
        : '/account/' + getOrganizationId(state) + '/9772/meetings/' + meeting.uuid + '/meetingOccurrences/' + e.uuid,
      classified: e.classified,
      restricted: e.restricted,
      meetingStatus: e.meetingStatus,
    };
  });
  let name = meeting.name;
  if (meeting.type === 'INTERVIEW_ACTIVE' || meeting.type === 'INTERVIEW_TERMINATED') {
    name = t('summaries.employeeInterviewWith', { name: meeting.interviewEmployeeName });
  }
  const defaultMeeting = getDefaultNewMeeting(commonData);
  return {
    ...defaultMeeting,
    promoData: getPromoDataForEntity('meetings', meeting, getFeatureStates(state)),
    parentHref: currentParent,
    uuid: meeting.uuid,
    name,
    today: LocalDate.fromString(state.today),
    occurrences,
  };
}

// TODO uklarhet om hvordan håndtere tidspunkt (klokkeslett)

export async function buildMeetingView(
  _api: ApiClient,
  uuid: string,
  commonData: CommonDataEntityView,
  state: State,
  currentParent: string,
): Promise<MeetingView> {
  const organization = getOrganization(state);
  assertIsDefined(organization);

  const list = meetings(state);
  const meeting = list.find((m) => m.uuid === uuid);
  assertEntityFound(meeting, 'meetings', uuid);

  return toMeetingView(state, commonData, currentParent, meeting);
}
